if (!customElements.get('product-card-info')) {
  customElements.define(
    'product-card-info',
    class ProductCardInfo extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.quickAddProductForm = this.querySelector('.js-product-card-quick-add-form');
        this.productId = this.dataset.productId;
        this.section = document.getElementById(`shopify-section-${this.sectionId}`);
        this.variantPickers = this.querySelectorAll(`[data-product-form-id="${this.productFormId}"]`);
        this.selectedSwatch = this.querySelector('input[data-swatch-button-class]:checked');

        if (this.selectedSwatch) {
          this.selectedSwatch.addEventListener('click', this.handleSelectedSwatchClick.bind(this));
        }
      }

      connectedCallback() {
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.dispatchEvent(new CustomEvent('product-card-info:loaded', { bubbles: true }));
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        this.optionIds = selectedOptionValues;
        const productUrl = event.target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const isCombinedListingProduct = this.dataset.url !== productUrl;

        // Don't handle combined listing products right now.
        if (isCombinedListingProduct) return;

        this.getVariantData(event);
      }

      toCurrency(value) {
        return (parseFloat(value) * 0.01).toFixed(2);
      }

      updateHtml(source, destination) {
        if (source && destination) destination.innerHTML = source.innerHTML;
      }

      getVariantData(event) {
        const params = this.optionIds.length > 0
          ? `&option_values=${this.optionIds.join(',')}`
          : '';

        fetch(`${this.dataset.url}?${params}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.currentVariant = JSON.parse(html.querySelector(`.js-main-product`).querySelector(`[data-selected-variant]`).textContent);

            // Update variant selectors to have unique input ids and label attributes to allow for multiple variant selectors on the same page
            const oldSectionId = html.querySelector('variant-selects').dataset.sectionId;

            let newProductCardVariantSelects = html.querySelector(`.js-main-product`).querySelector('.js-product-variant-picker-container');

            newProductCardVariantSelects.querySelectorAll('label').forEach((label) => {
              const oldFor = label.getAttribute('for');
              const newFor = oldFor.replace(oldSectionId, this.sectionId);
              label.setAttribute('for', newFor);
            });

            newProductCardVariantSelects.querySelectorAll('input[type="radio"]').forEach((radioInput) => {
              const oldName = radioInput.getAttribute('name');
              const newName = oldName.replace(oldSectionId, this.sectionId);
              radioInput.setAttribute('name', newName);

              const oldId = radioInput.getAttribute('id');
              const newId = oldId.replace(oldSectionId, this.sectionId);
              radioInput.setAttribute('id', newId);
            });

            if (!this.currentVariant) {
              this.toggleAddButton(true, '', true);
              this.setUnavailable();
            } else {
              this.renderProductInfo(event, newProductCardVariantSelects);
              this.updateVariantOnSubmitButton();
              this.updateSwatchButtons(event);
            }
          });
      }

      // Get the info for the current variant and render it on the card
      renderProductInfo(event, newProductCardVariantSelects) {
        const productCardVariantSelectsContainer = event.target.closest('.js-product-variant-picker-container');
        productCardVariantSelectsContainer.innerHTML = newProductCardVariantSelects.innerHTML;

        this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        this.updateMedia();
        this.updateLinksInProductCard();
        this.removeErrorMessage();
      }

      handleSelectedSwatchClick() {
        this.updateMedia();
        this.selectedSwatch.removeEventListener('click', this.handleSelectedSwatchClick.bind(this));
      }

      updateMedia() {
        if (!this.currentVariant) return;
        if (!this.currentVariant.featured_media) return;

        const mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}-${this.productId}`);
        const productCardMediaGallery = document.getElementById(`ProductCardMediaGallery-${this.sectionId}-${this.productId}`);

        if (mediaGallery) {
          mediaGallery.setActiveMedia(`${this.sectionId}-${this.productId}-${this.currentVariant.featured_media.id}`, true);
          mediaGallery.updateThumbnail(this.currentVariant.featured_media.id);
        } else if (productCardMediaGallery) {
          productCardMediaGallery.setActiveMedia(`${this.sectionId}-${this.productId}-${this.currentVariant.featured_media.id}`, true);
        }
      }

      updateLinksInProductCard() {
        let url = new URL(`${this.dataset.shopUrl}${this.dataset.url}`);
        let params = new URLSearchParams(url.search);
        params.set('variant', this.currentVariant.id);
        url.search = params.toString();

        this.querySelectorAll('[data-product-link]').forEach((link) => {
          link.setAttribute('href', url.href);
        });
      }

      // Updates the input values so the correct variant is added to the cart
      updateVariantOnSubmitButton() {
        const input = this.querySelector('input[data-product-card-id]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      removeErrorMessage() {
        const productFormElement = this.closest('product-form');
        if (productFormElement) productFormElement.handleErrorMessage();
      }

      updateSwatchButtons(event) {
        const swatchButtons = this.querySelector('product-card-swatches').querySelectorAll('.variant-picker__swatches-button');
        const selectedSwatchButton = this.querySelector(`.${event.target.dataset.swatchButtonClass}`);

        if (!selectedSwatchButton) return;
        swatchButtons.forEach((swatchButton) => {
          swatchButton.classList.remove('active-swatch');
        });
        selectedSwatchButton.classList.add('active-swatch');
      }

      // Update product forms based on variant change, and disable if variant is sold out or unavailable
      toggleAddButton(disable = true, text, modifyClass = true) {
        const addButton = this.quickAddProductForm.querySelector('[data-submit-button]');
        const addButtonText = this.quickAddProductForm.querySelector('[data-submit-button-text]');
        const buttonPrice = this.quickAddProductForm.querySelector('[data-button-price]');
        const buttonPriceContainer = this.quickAddProductForm.querySelector('[data-button-price-container]');
        const quantityActionElements = this.quickAddProductForm.querySelectorAll('[data-quantity-action-element]');

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
          if (quantityActionElements) quantityActionElements.forEach((element) => element.setAttribute('disabled', true));

          if (this.currentVariant) {
            if (buttonPriceContainer) buttonPriceContainer.classList.remove('display--none');
            if (buttonPrice) buttonPrice.textContent = this.toCurrency(this.currentVariant.price);
          } else {
            if (buttonPriceContainer) buttonPriceContainer.classList.add('display--none');
          }
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = addButton.dataset.addToCartLabel || window.variantStrings.addToCart;
          if (buttonPriceContainer) buttonPriceContainer.classList.remove('display--none');
          if (buttonPrice) buttonPrice.textContent = this.toCurrency(this.currentVariant.price);
        }
      }

      // Indicates if the variant selected is unavailable
      setUnavailable() {
        const addButton = this.quickAddProductForm.querySelector('[data-submit-button]');
        const addButtonText = this.quickAddProductForm.querySelector('[data-submit-button-text]');
        const buttonPriceContainer = this.quickAddProductForm.querySelector('[data-button-price-container]');

        if (buttonPriceContainer) buttonPriceContainer.classList.add('display--none');
        if (addButton) addButtonText.textContent = window.variantStrings.unavailable;
      }
    }
  );
}

if (!customElements.get('product-card-swatches')) {
  customElements.define(
    'product-card-swatches',
    class ProductCardSwatches extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;
        this.section = document.getElementById(`shopify-section-${this.sectionId}`);
        this.swatches = this.querySelectorAll('.variant-picker__swatches-button');
        this.productCardInfo = this.closest('product-card-info');

        this.swatches.forEach((swatch) => {
          swatch.addEventListener('click', this.handleSwatchClick.bind(this));
        });
      }

      handleSwatchClick(event) {
        const swatchButton = event.target.closest('.variant-picker__swatches-button');

        this.swatches.forEach((swatch) => {
          swatch.classList.remove('active-swatch');
        });

        swatchButton.classList.add('active-swatch');
        const swatchLabel = this.productCardInfo.querySelector(`.${swatchButton.dataset.swatchLabelClass}`);
        swatchLabel.click();

      }
    }
  );
}